import React, { useEffect, useRef } from 'react';

import ReactDOM from 'react-dom/client';

import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorCommand, EditorInjectionAPI } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import {
	EditorState,
	NodeSelection,
	PluginKey,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { EditorView } from '@atlaskit/editor-prosemirror/view';
// Will be correctly styled in COMPHUB-3147
// eslint-disable-next-line  @atlaskit/design-system/no-emotion-primitives
import { Box, xcss } from '@atlaskit/primitives';

import type {
	CompanyHubSectionCtaPluginSharedState,
	CompanyHubSectionCtaPlugin as CompanyHubSectionCtaPluginType,
} from './companyHubSectionCtaPluginType';
import { newSectionNode } from './ui/constants/hubElementPlaceholderConstants';
import { PlusButton } from './ui/PlusButton';

export const companyHubSectionCtaPluginKey = new PluginKey('companyHubSectionCta');

const initialContainerStyles = xcss({
	pointerEvents: 'none', // Initially hidden / non-interactive
	position: 'absolute',
	zIndex: 'layer',
});

const isValidInsertPosition = (state: EditorState, pos: number): boolean => {
	const { schema, doc } = state;
	const $pos = doc.resolve(pos);

	const isParentDoc = $pos.parent.type === schema.nodes.doc;
	return (
		isParentDoc &&
		$pos.parent.canReplaceWith($pos.index(), $pos.index(), schema.nodes.multiBodiedExtension)
	);
};

// Create a proper React component for the content section
const SectionButtonManager = ({
	editorView,
	api,
}: {
	editorView: EditorView;
	api: EditorInjectionAPI<any, any> | undefined;
}) => {
	const hoverPosition = useSharedPluginStateSelector(api, 'companyHubSectionCtaPlugin.hoveredPos');
	const persistentButtonContainerRef = useRef<HTMLDivElement | null>(null);
	const buttonReactRootRef = useRef<ReactDOM.Root | null>(null); // For the stable React root
	const buttonHostElementRef = useRef<HTMLDivElement | null>(null); // For the stable div that hosts the React root

	// Effect for one-time DOM setup and teardown.
	// This creates the host element and React root that the button will be rendered into.
	// It runs only once when the component mounts and cleans up when it unmounts.
	useEffect(() => {
		if (!editorView || !persistentButtonContainerRef.current) {
			return;
		}

		if (!buttonHostElementRef.current) {
			const host = document.createElement('div');
			host.style.width = '100%';
			persistentButtonContainerRef.current.appendChild(host);
			buttonHostElementRef.current = host;
		}
		if (!buttonReactRootRef.current && buttonHostElementRef.current) {
			buttonReactRootRef.current = ReactDOM.createRoot(buttonHostElementRef.current);
		}

		return () => {
			// Cleanup on component unmount or when editorView/api changes
			if (buttonReactRootRef.current) {
				buttonReactRootRef.current.unmount();
				buttonReactRootRef.current = null;
			}
			if (buttonHostElementRef.current?.parentElement) {
				buttonHostElementRef.current.parentElement.removeChild(buttonHostElementRef.current);
				buttonHostElementRef.current = null;
			}
		};
	}, [editorView]);

	// Effect for positioning, visibility, and re-rendering the button.
	// This runs every time the hover position changes.
	useEffect(() => {
		if (!editorView || !persistentButtonContainerRef.current || !buttonReactRootRef.current) {
			return;
		}

		const container = persistentButtonContainerRef.current;
		const { dom: editorDom } = editorView;

		const handleClick = (event: React.MouseEvent) => {
			// prevent default and stop propagation to avoid any unintended behavior
			event.preventDefault();
			event.stopPropagation();

			const currentPluginState = companyHubSectionCtaPluginKey.getState(editorView.state);
			const insertPos = currentPluginState?.hoveredPos;

			if (insertPos !== null && insertPos !== undefined) {
				if (api?.companyHubSectionCtaPlugin?.commands?.insertSection) {
					const command = api.companyHubSectionCtaPlugin.commands.insertSection(insertPos);
					command(editorView.state, editorView.dispatch, editorView);
				}
			}
		};

		if (hoverPosition !== null) {
			container.style.display = 'flex';

			const coords = editorView.coordsAtPos(hoverPosition);
			const { top: viewportRelativeTop } = coords;

			const offsetParent = editorDom.offsetParent || document.body;
			const offsetParentRect = offsetParent.getBoundingClientRect();

			let relativeTop = viewportRelativeTop - offsetParentRect.top;
			if (offsetParent && typeof offsetParent.scrollTop === 'number') {
				relativeTop += offsetParent.scrollTop;
			}

			const buttonHeight = 32;
			container.style.top = `${relativeTop - buttonHeight}px`;

			container.style.left = `calc(50vw - ${offsetParentRect.left}px)`;
			container.style.width = '100vw';
			container.style.transform = 'translateX(-50%)';
			container.style.pointerEvents = 'auto';

			// By providing a key, we force React to create a new instance of PlusButton,
			// which resets the internal state of the tooltip.
			buttonReactRootRef.current.render(<PlusButton key={hoverPosition} onClick={handleClick} />);
		} else {
			container.style.display = 'none';
		}
	}, [editorView, hoverPosition, api]);

	return <Box ref={persistentButtonContainerRef} xcss={initialContainerStyles} />;
};

const MemoizedSectionButtonManager = React.memo(SectionButtonManager);

export const companyHubSectionCtaPlugin: CompanyHubSectionCtaPluginType = ({ api }) => {
	return {
		name: 'companyHubSectionCtaPlugin',
		pmPlugins() {
			return [
				{
					name: 'companyHubSectionCtaButton',
					plugin: (options) => {
						let rAFId: number | null = null;
						let lastMouseEvent: MouseEvent | null = null;

						const processMouseMoveCallback = (view: EditorView) => {
							rAFId = null; // Reset rAFId, so a new frame can be scheduled

							// Testing revealed that the editorView.dom.isConnected property is not always available
							if (!lastMouseEvent || !view.dom.isConnected) {
								return;
							}

							const event = lastMouseEvent; // Use the stored event

							const coords = { left: event.clientX, top: event.clientY };
							const posResult = view.posAtCoords(coords);
							let newHoveredPos = posResult ? posResult.pos : null;

							if (newHoveredPos !== null) {
								if (!isValidInsertPosition(view.state, newHoveredPos)) {
									newHoveredPos = null;
								}
							}

							const pluginState = companyHubSectionCtaPluginKey.getState(view.state);
							const currentPos = pluginState ? pluginState.hoveredPos : null;

							if (currentPos === newHoveredPos) {
								return; // No change, do nothing further
							}

							const { state, dispatch } = view;
							dispatch(
								state.tr.setMeta(companyHubSectionCtaPluginKey, { hoveredPos: newHoveredPos }),
							);
						};

						return new SafePlugin({
							key: companyHubSectionCtaPluginKey,

							// The plugin state simply stores the current hovered position (or null)
							state: {
								init() {
									return { hoveredPos: null };
								},
								apply(tr, pluginState) {
									const meta = tr.getMeta(companyHubSectionCtaPluginKey);
									if (meta && meta.hoveredPos !== undefined) {
										return { hoveredPos: meta.hoveredPos };
									}
									return pluginState;
								},
							},

							props: {
								handleDOMEvents: {
									mousemove(view, event: Event) {
										lastMouseEvent = event as MouseEvent; // Store the latest event
										if (rAFId === null) {
											// If no rAF is currently pending
											rAFId = requestAnimationFrame(() => processMouseMoveCallback(view));
										}
										return false; // Indicate event is handled by this plugin
									},
								},
							},
							view: (editorView) => {
								return {
									destroy() {
										if (rAFId !== null) {
											cancelAnimationFrame(rAFId);
											rAFId = null;
										}
									},
								};
							},
						});
					},
				},
			];
		},
		getSharedState(editorState?: EditorState): CompanyHubSectionCtaPluginSharedState | undefined {
			if (!editorState) {
				return undefined;
			}
			const pmPluginState = companyHubSectionCtaPluginKey.getState(editorState);
			if (!pmPluginState) {
				return undefined;
			}
			return {
				hoveredPos: pmPluginState.hoveredPos,
			};
		},
		commands: {
			insertSection: (position: number): EditorCommand => {
				return ((state: EditorState, dispatch?: (tr: Transaction) => void) => {
					if (dispatch) {
						const { tr } = state;
						const nodeToInsert = newSectionNode(state);
						tr.insert(position, nodeToInsert);

						const selection = NodeSelection.create(tr.doc, position);
						tr.setSelection(selection);

						dispatch(tr);
					}
					return true;
				}) as unknown as EditorCommand;
			},
		},
		contentComponent({ editorView }) {
			return <MemoizedSectionButtonManager editorView={editorView} api={api} />;
		},
	};
};
